'use client';

import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { type Editor, type EditorStateSnapshot, useEditorState } from '@tiptap/react';
import { useCallback } from 'react';

// --- Hooks ---
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Icons ---
import { ListIcon } from '@/components/tiptap-editor/tiptap-icons/list-icon';
import { ListOrderedIcon } from '@/components/tiptap-editor/tiptap-icons/list-ordered-icon';
import { ListTodoIcon } from '@/components/tiptap-editor/tiptap-icons/list-todo-icon';

// --- Lib ---
import {
    getSelectedBlockNodes,
    isNodeInSchema,
    isNodeTypeSelected,
    isValidPosition,
    selectionWithinConvertibleTypes,
} from '@/lib/tiptap-utils';
import { CONVERTIBLE_BLOCK_TYPES, findClosestListOrConvertibleBlock, LIST_NODE_TYPES } from '@/lib/zy-tiptap-utils';

export type ListType = 'bulletList' | 'orderedList' | 'taskList';

export type OrderedListType = '1' | 'a' | 'i';

/**
 * Configuration for the list functionality
 */
export interface UseListConfig {
    /**
     * The Tiptap editor instance.
     */
    editor?: Editor | null;
    /**
     * The type of list to toggle.
     */
    type: ListType;
    /**
     * For orderedList, the HTML ol type attribute (e.g. 'a' for a, b, c).
     */
    orderedListType?: OrderedListType;
    /**
     * Whether the button should hide when list is not available.
     * @default false
     */
    hideWhenUnavailable?: boolean;
    /**
     * Callback function called after a successful toggle.
     */
    onToggled?: () => void;
}

export const listIcons = {
    bulletList: ListIcon,
    orderedList: ListOrderedIcon,
    taskList: ListTodoIcon,
};

export const listLabels: Record<ListType, string> = {
    bulletList: 'Bullet List',
    orderedList: 'Number List',
    taskList: 'Task List',
};

const orderedListTypeLabels: Partial<Record<OrderedListType, string>> = {
    a: 'Letter List',
};

function getListLabel(type: ListType, orderedListType?: OrderedListType): string {
    if (type === 'orderedList' && orderedListType && orderedListTypeLabels[orderedListType]) {
        return orderedListTypeLabels[orderedListType];
    }
    return listLabels[type];
}

export const LIST_SHORTCUT_KEYS: Record<ListType, string> = {
    bulletList: 'mod+shift+8',
    orderedList: 'mod+shift+7',
    taskList: 'mod+shift+9',
};

export const getListShortcutKeys = (type: ListType, orderedListType?: OrderedListType): string => {
    if (type === 'orderedList' && orderedListType) {
        return `mod+shift+${orderedListType === '1' ? '7' : '9'}`;
    }
    return LIST_SHORTCUT_KEYS[type];
};

/**
 * Checks if a list can be toggled in the current editor state
 */
export function canToggleList(editor: Editor | null, type: ListType, turnInto: boolean = true): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!isNodeInSchema(type, editor) || isNodeTypeSelected(editor, ['image'])) return false;

    if (!turnInto) {
        try {
            switch (type) {
                case 'bulletList':
                    return editor.can().toggleBulletList();
                case 'orderedList':
                    return editor.can().toggleOrderedList();
                case 'taskList':
                    return editor.can().toggleList('taskList', 'taskItem');
                default:
                    return false;
            }
        } catch {
            return false;
        }
    }

    // Ensure selection is in nodes we're allowed to convert
    if (
        !selectionWithinConvertibleTypes(editor, [
            'paragraph',
            'heading',
            'bulletList',
            'orderedList',
            'taskList',
            'blockquote',
            'codeBlock',
        ])
    )
        return false;

    // Either we can set list directly on the selection,
    // or we can clear formatting/nodes to arrive at a list.
    try {
        switch (type) {
            case 'bulletList':
                return editor.can().toggleBulletList() || editor.can().clearNodes();
            case 'orderedList':
                return editor.can().toggleOrderedList() || editor.can().clearNodes();
            case 'taskList':
                return editor.can().toggleList('taskList', 'taskItem') || editor.can().clearNodes();
            default:
                return false;
        }
    } catch {
        return false;
    }
}

/**
 * Checks if list is currently active
 */
export function isListActive(editor: Editor | null, type: ListType, orderedListType?: OrderedListType): boolean {
    if (!editor || !editor.isEditable) return false;

    switch (type) {
        case 'bulletList':
            return editor.isActive('bulletList');
        case 'orderedList':
            if (orderedListType != null) {
                return editor.isActive('orderedList', { type: orderedListType });
            }
            if (!editor.isActive('orderedList')) return false;
            return (
                editor.getAttributes('orderedList').type == null ||
                editor.getAttributes('orderedList').type === '' ||
                editor.getAttributes('orderedList').type === '1'
            );
        case 'taskList':
            return editor.isActive('taskList');
        default:
            return false;
    }
}

/**
 * Toggles list in the editor
 */
export function toggleList(editor: Editor | null, type: ListType, orderedListType?: OrderedListType): boolean {
    if (!editor || !editor.isEditable) return false;
    if (!canToggleList(editor, type, true)) return false;

    try {
        const view = editor.view;
        let state = view.state;
        let tr = state.tr;

        const blocks = getSelectedBlockNodes(editor);

        // Switching between ordered list sub-types (e.g. numbered ↔ lettered) only
        if (type === 'orderedList' && editor.isActive('orderedList') && !isListActive(editor, 'orderedList', orderedListType)) {
            editor
                .chain()
                .focus()
                .updateAttributes('orderedList', { type: orderedListType ?? null })
                .selectTextblockEnd()
                .run();
            return true;
        }

        const isPossibleToTurnInto =
            selectionWithinConvertibleTypes(editor, [...LIST_NODE_TYPES, ...CONVERTIBLE_BLOCK_TYPES]) && blocks.length === 1;

        if ((state.selection.empty || state.selection instanceof TextSelection) && isPossibleToTurnInto) {
            const target = findClosestListOrConvertibleBlock(state.selection.$anchor);
            if (!target || !isValidPosition(target.pos)) return false;

            const $at = state.doc.resolve(target.pos);
            const ancestorTypes: string[] = [];
            for (let d = 0; d <= $at.depth; d++) ancestorTypes.push($at.node(d).type.name);
            const insideZyProblemOrSolution = ancestorTypes.some(t => t === 'zyProblem');
            if (!insideZyProblemOrSolution) {
                tr = tr.setSelection(NodeSelection.create(state.doc, target.pos));
                view.dispatch(tr);
                state = view.state;
            }
        }

        const selection = state.selection;

        let chain = editor.chain().focus();

        if (selection instanceof NodeSelection) {
            const firstChild = selection.node.firstChild?.firstChild;
            const lastChild = selection.node.lastChild?.lastChild;

            const from = firstChild ? selection.from + firstChild.nodeSize : selection.from + 1;

            const to = lastChild ? selection.to - lastChild.nodeSize : selection.to - 1;

            const resolvedFrom = state.doc.resolve(from);
            const resolvedTo = state.doc.resolve(to);

            chain = chain.setTextSelection(TextSelection.between(resolvedFrom, resolvedTo)).clearNodes();
        }

        const inOrderedListWithType =
            type === 'orderedList' && orderedListType != null && editor.isActive('orderedList', { type: orderedListType });
        const inOrderedListOtherType = type === 'orderedList' && orderedListType != null && editor.isActive('orderedList');

        if (type === 'orderedList' && orderedListType != null) {
            if (inOrderedListWithType) {
                chain.liftListItem('listItem').lift('bulletList').lift('orderedList').lift('taskList').run();
            } else if (inOrderedListOtherType) {
                chain.updateAttributes('orderedList', { type: orderedListType }).run();
            } else {
                chain.toggleList('orderedList', 'listItem', false, { type: orderedListType }).run();
            }
        } else if (editor.isActive(type)) {
            chain.liftListItem('listItem').lift('bulletList').lift('orderedList').lift('taskList').run();
        } else {
            const toggleMap: Record<ListType, () => typeof chain> = {
                bulletList: () => chain.toggleBulletList(),
                orderedList: () => chain.toggleOrderedList(),
                taskList: () => chain.toggleList('taskList', 'taskItem'),
            };

            const toggle = toggleMap[type];
            if (!toggle) return false;

            toggle().run();
        }

        editor.chain().focus().selectTextblockEnd().run();

        return true;
    } catch (error) {
        console.log(error);
        return false;
    }
}

/**
 * Determines if the list button should be shown
 */
export function shouldShowButton(props: {
    editor: Editor | null;
    type: ListType;
    hideWhenUnavailable: boolean;
    orderedListType?: OrderedListType;
}): boolean {
    const { editor, type, hideWhenUnavailable } = props;

    if (!editor || !editor.isEditable) return false;
    if (!isNodeInSchema(type, editor)) return false;

    if (hideWhenUnavailable && !editor.isActive('code')) {
        return canToggleList(editor, type, true);
    }

    return true;
}

/**
 * Custom hook that provides list functionality for Tiptap editor
 *
 * @example
 * ```tsx
 * // Simple usage
 * function MySimpleListButton() {
 *   const { isVisible, handleToggle, isActive } = useList({ type: "bulletList" })
 *
 *   if (!isVisible) return null
 *
 *   return <button onClick={handleToggle}>Bullet List</button>
 * }
 *
 * // Advanced usage with configuration
 * function MyAdvancedListButton() {
 *   const { isVisible, handleToggle, label, isActive } = useList({
 *     type: "orderedList",
 *     editor: myEditor,
 *     hideWhenUnavailable: true,
 *     onToggled: () => console.log('List toggled!')
 *   })
 *
 *   if (!isVisible) return null
 *
 *   return (
 *     <MyButton
 *       onClick={handleToggle}
 *       aria-label={label}
 *       aria-pressed={isActive}
 *     >
 *       Toggle List
 *     </MyButton>
 *   )
 * }
 * ```
 */
type ListToolbarSnapshot = {
    isVisible: boolean;
    isActive: boolean;
    canToggle: boolean;
};

function listToolbarSnapshotEquality(a: ListToolbarSnapshot, b: ListToolbarSnapshot | null): boolean {
    return a.isVisible === b?.isVisible && a.isActive === b?.isActive && a.canToggle === b?.canToggle;
}

export function useList(config: UseListConfig) {
    const { editor: providedEditor, type, orderedListType, hideWhenUnavailable = false, onToggled } = config;

    const { editor } = useTiptapEditor(providedEditor);

    const listToolbarSelector = useCallback(
        (ctx: EditorStateSnapshot<Editor | null>): ListToolbarSnapshot => {
            const ed = ctx.editor;
            if (!ed) {
                return { isVisible: false, isActive: false, canToggle: false };
            }
            return {
                isVisible: shouldShowButton({ editor: ed, type, hideWhenUnavailable, orderedListType }),
                isActive: isListActive(ed, type, orderedListType),
                canToggle: canToggleList(ed, type, true),
            };
        },
        [type, orderedListType, hideWhenUnavailable],
    );

    const listToolbar = useEditorState({
        editor,
        selector: listToolbarSelector,
        equalityFn: listToolbarSnapshotEquality,
    });

    const isVisible = listToolbar?.isVisible ?? false;
    const isActive = listToolbar?.isActive ?? false;
    const canToggle = listToolbar?.canToggle ?? false;

    const handleToggle = useCallback(() => {
        if (!editor) return false;

        const success = toggleList(editor, type, orderedListType);
        if (success) {
            onToggled?.();
        }
        return success;
    }, [editor, type, orderedListType, onToggled]);

    return {
        isVisible,
        isActive,
        handleToggle,
        canToggle,
        label: getListLabel(type, orderedListType),
        shortcutKeys: getListShortcutKeys(type, orderedListType),
        Icon: listIcons[type],
    };
}
